(function () {
  'use strict';

  // ─── SearchTap bridge ──────────────────────────────────────────────────────
  window.pendingShopifyFilters = window.pendingShopifyFilters || [];

  window.enqueueShopifyFilter = function (filter, url) {
    if (window.applyShopifyFilter) {
      window.applyShopifyFilter(filter, url);
      return;
    }
    window.pendingShopifyFilters.push({ filter, url });
    drainShopifyFilters();
  };

  // Nothing ever drained this queue, so a call that arrived before the SDK
  // mounted was dropped silently — and on shop-all the SDK is idle-loaded, so
  // that window is seconds wide. Flush once the SDK installs its handler.
  // Only the newest entry is applied: each queued url is a whole-state url, so
  // replaying older ones would just fire superseded searches.
  let isDrainingShopifyFilters = false;

  function drainShopifyFilters() {
    if (isDrainingShopifyFilters) return;
    isDrainingShopifyFilters = true;

    whenSearchtapReady(() => {
      isDrainingShopifyFilters = false;
      const queued = window.pendingShopifyFilters.splice(0);
      const latest = queued[queued.length - 1];
      if (latest && window.applyShopifyFilter) {
        window.applyShopifyFilter(latest.filter, latest.url);
      }
    });
  }

  // ─── Bubble tabs ──────────────────────────────────────────────────────────
  // The bubbles are theme markup (st-collection-filter.liquid), not an SDK
  // component, so a click has to reach into the SDK's own state to stay in sync
  // with the sidebar and the sort dropdown.
  //
  // Everything on the page is one search request built from SearchPage's
  // `filters` array plus its `sortLabel` — and the store's
  // `filterArrayCollection` is that same array once a response has landed
  // (setFilterArray(this.filters) hands it over by reference), so editing a
  // group's `selected` here is the exact mutation a sidebar checkbox makes.
  // SearchPage.pushFilters() then rebuilds the url from the
  // full current state (Bt.clear() drops only filters, so the active sort
  // survives in the query string) and re-runs the search.
  //
  // This used to hand-build a url and feed it to the SDK's
  // window.applyShopifyFilter bridge instead, which had two consequences:
  //
  //   1. The active sort was left out of the address bar while still applied to
  //      the results, so refreshing or sharing the link returned the same
  //      products in a different order.
  //   2. The bridge only takes its filter path when the url contains the
  //      literal "?f." — with a sort (or a utm_ param) sitting ahead of
  //      f.Product+Type in the query string it fell through to clearFilters()
  //      and wiped every filter instead of applying the tab.

  const PRODUCT_TYPE_TITLE = 'Product Type';
  const BUBBLE_SELECTOR = '.st-collection-bubble-filter';

  function findProductTypeFilter(filterArray) {
    return (filterArray ?? []).find((f) => f.title === PRODUCT_TYPE_TITLE) ?? null;
  }

  // data-url-value is the query-string form ('lip+balm'); facet values carry the
  // label the API indexed ('Lip Balm'). Prefer the real label so the pushed url
  // and the sidebar checkbox agree, and fall back to the spaced-out tab value
  // when the facets have not arrived yet — that lowercase form is what
  // getSearchParamsFromUrl() itself assigns when the SDK reads a filter off a url.
  function resolveProductTypeLabel(filter, urlValue) {
    const target = urlValue.toLowerCase().replace(/\+/g, ' ');

    for (const value of filter.values ?? []) {
      const label = typeof value === 'string' ? value : (value.label ?? '');
      if (label.toLowerCase() === target) return label;
    }

    return target;
  }

  function applyBubbleTab(urlValue) {
    const proxy = getSearchPageProxy();
    if (!proxy) return false;

    // pushFilters() leaves currPage alone on a collection page, and the SDK's
    // own bridge resets it first — do the same so a tab picked on page 3 does
    // not carry that page number into the new result set.
    proxy.currPage = 1;

    // "All" → the SDK's own clear-all: empties every group, resets the price
    // slider, re-pushes the url through pushFilters() and marks this bubble
    // active. It never touches sortLabel, so the active sort stays applied.
    // It no-ops when nothing is selected, which is the correct outcome anyway.
    if (urlValue === '') {
      proxy.clearFilters();
      return true;
    }

    // Read the group off SearchPage's own `filters` — the array pushFilters()
    // actually builds its request from, and the one setFilterArray(this.filters)
    // hands to the store by reference. It exists from mount (filters:
    // xt.filterValues), whereas the store's copy only appears after the first API
    // response. On mobile the SDK skips its initial search entirely — mounted()
    // gates init() behind matchMedia('(min-width: 769px)') — so
    // filterArrayCollection sits at [] until the shopper opens the filter drawer,
    // and reading the group off the store there always came back null.
    const filter = findProductTypeFilter(proxy.filters);
    if (!filter) return false;

    // A tab replaces the group rather than adding to it.
    filter.selected = [resolveProductTypeLabel(filter, urlValue)];

    // pushFilters() directly rather than store.setCloseInitialContent(true):
    // the SDK's watcher on that flag also runs
    // `isDeviceMobile() && (this.isFilterOpen = true)`, which would throw the
    // mobile filter drawer open on the first tab tap.
    proxy.pushFilters();
    return true;
  }

  function markActiveBubble(activeBubble) {
    for (const bubble of document.querySelectorAll(BUBBLE_SELECTOR)) {
      bubble.classList.toggle('active', bubble === activeBubble);
    }
  }

  const BUBBLE_RETRY_INTERVAL = 100;
  const BUBBLE_RETRY_MAX_ATTEMPTS = 25;

  let pendingBubbleUrlValue = null;
  let bubbleRetryPoll = null;

  // applyBubbleTab() only fails while the SDK has not mounted yet, and on
  // shop-all the bundle is idle-loaded, so that window is seconds wide.
  // whenSearchtapReady() reports ready the moment store + proxy exist, which used
  // to leave a gap: a tap that landed after mount but before the facets arrived
  // re-failed once and was dropped. Retry for a couple of seconds instead.
  function applyBubbleTabWhenPossible(urlValue) {
    pendingBubbleUrlValue = urlValue;

    const attempt = () => {
      if (pendingBubbleUrlValue === null) return true;
      if (!applyBubbleTab(pendingBubbleUrlValue)) return false;
      pendingBubbleUrlValue = null;
      return true;
    };

    if (attempt()) return;

    // A tap while a retry is already running just replaces the pending value —
    // last tab tapped wins, same as before.
    if (bubbleRetryPoll) return;

    let attempts = 0;
    bubbleRetryPoll = setInterval(() => {
      attempts++;
      if (attempt()) {
        clearInterval(bubbleRetryPoll);
        bubbleRetryPoll = null;
        return;
      }
      if (attempts >= BUBBLE_RETRY_MAX_ATTEMPTS) {
        clearInterval(bubbleRetryPoll);
        bubbleRetryPoll = null;
        pendingBubbleUrlValue = null;
      }
    }, BUBBLE_RETRY_INTERVAL);
  }

  function initBubbleTabs() {
    const wrap = document.querySelector('.searchtap_bubble_filter_wrap');
    if (!wrap) return;

    wrap.addEventListener('click', (event) => {
      const bubble = event.target.closest(BUBBLE_SELECTOR);
      if (!bubble) return;

      const urlValue = bubble.dataset.urlValue ?? '';

      // Mark the tab straight away — the SDK is idle-loaded on shop-all, so the
      // search behind it can be seconds away. syncBubbleTabActiveState()
      // reconciles against real state on the next API response.
      markActiveBubble(bubble);
      applyBubbleTabWhenPossible(urlValue);
    });
  }

  // ─── Infinite scroll ──────────────────────────────────────────────────────
  let paginationObserver = null;
  let isFetching = false;
  let scrollListener = null;

  function checkPaginationInView() {
    if (isFetching) return;
    const pagination = document.querySelector('#product-grid-Pagination');
    if (!pagination) return;
    const anchor = pagination.querySelector('a');
    if (!anchor) return;
    const rect = pagination.getBoundingClientRect();
    if (rect.top < window.innerHeight + 200) {
      window.onClickCollectionPagination(anchor, null);
    }
  }

  function initPaginationObserver() {
    const pagination = document.querySelector('#product-grid-Pagination');

    // Remove scroll listener and disconnect any existing IntersectionObserver
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener, { passive: true });
      scrollListener = null;
    }
    if (paginationObserver) {
      paginationObserver.disconnect();
      paginationObserver = null;
    }

    if (!pagination) return;
    if (!pagination.querySelector('a')) return;

    // Check immediately in case the element is already in view after a page load
    checkPaginationInView();

    // IntersectionObserver as primary trigger
    paginationObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching) {
          paginationObserver.disconnect();
          paginationObserver = null;
          checkPaginationInView();
        }
      },
      { rootMargin: '200px' }
    );
    paginationObserver.observe(pagination);

    // Scroll listener as fallback — fires when observer misses a rapid scroll
    scrollListener = () => checkPaginationInView();
    window.addEventListener('scroll', scrollListener, { passive: true });
  }

  window.onClickCollectionPagination = function (anchor, event) {
    if (event) event.preventDefault();
    if (isFetching) return;

    isFetching = true;
    const url = anchor.href;
    const paginationEl = document.querySelector('#product-grid-Pagination');
    if (paginationEl) paginationEl.classList.add('is-loading');

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const srcGrid = doc.querySelector('#product-grid');
        const tgtGrid = document.querySelector('#product-grid');
        if (srcGrid && tgtGrid) {
          [...srcGrid.children].forEach((child) => {
            tgtGrid.appendChild(child.cloneNode(true));
          });
        }

        const srcPagination = doc.querySelector('#product-grid-Pagination');
        const currentPagination = document.querySelector('#product-grid-Pagination');
        if (currentPagination) {
          if (srcPagination) {
            currentPagination.replaceWith(srcPagination.cloneNode(true));
          } else {
            currentPagination.remove();
          }
        }

        isFetching = false;
        initPaginationObserver();
      })
      .catch((err) => {
        console.error('[searchtap-collection]', err);
        isFetching = false;
        // Re-init observer so the user can retry by scrolling past the spinner
        initPaginationObserver();
      });
  };

  // ─── SearchPage proxy helper ─────────────────────────────────────────────
  function getSearchPageProxy() {
    const container = document.getElementById('collectionmodalcontainer');
    const rootVNode = container?._vnode;
    if (!rootVNode?.component) return null;

    function walk(vnode) {
      if (!vnode || typeof vnode !== 'object') return null;
      if (vnode.component) {
        if (vnode.component.type?.name === 'SearchPage') return vnode.component.proxy;
        const found = walk(vnode.component.subTree);
        if (found) return found;
      }
      if (Array.isArray(vnode.children)) {
        for (const child of vnode.children) {
          const found = walk(child);
          if (found) return found;
        }
      }
      return null;
    }

    return walk(rootVNode.component.subTree);
  }

  // ─── Pinia store readiness ────────────────────────────────────────────────
  const STORE_POLL_INTERVAL = 60;
  const STORE_POLL_MAX_ATTEMPTS = 200;

  function getSidebarStore() {
    const el = document.getElementById('collectionmodalcontainer');
    return el?.__vue_app__?.config.globalProperties.$pinia?._s.get('sidebarContent') ?? null;
  }

  function whenStoreReady(callback) {
    const store = getSidebarStore();
    if (store) {
      callback(store);
      return;
    }

    // Poll fast — the check is a couple of property reads, and a long interval
    // risks missing the SDK's first API response (which patchShowContent needs).
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const readyStore = getSidebarStore();
      if (readyStore) {
        clearInterval(poll);
        callback(readyStore);
        return;
      }
      if (attempts >= STORE_POLL_MAX_ATTEMPTS) clearInterval(poll);
    }, STORE_POLL_INTERVAL);
  }

  // Driving the SDK's filter state needs both halves: the store for the filter
  // objects and the SearchPage proxy for pushFilters()/clearFilters().
  function isSearchtapReady() {
    return Boolean(getSidebarStore() && getSearchPageProxy());
  }

  function whenSearchtapReady(callback) {
    if (isSearchtapReady()) {
      callback();
      return;
    }

    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (isSearchtapReady()) {
        clearInterval(poll);
        callback();
        return;
      }
      if (attempts >= STORE_POLL_MAX_ATTEMPTS) clearInterval(poll);
    }, STORE_POLL_INTERVAL);
  }

  // ─── Filter bridge patch ──────────────────────────────────────────────────
  function syncFilterSelectionFromUrl(store) {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = {};

    params.forEach((value, key) => {
      if (!key.startsWith('f.')) return;
      const title = key.slice(2);
      if (!urlFilters[title]) urlFilters[title] = new Set();
      urlFilters[title].add(value.toLowerCase());
    });

    if (!store.filterArrayCollection?.length) return;

    store.filterArrayCollection.forEach((filter) => {
      if (filter.type !== 'textFacet') return;
      const urlSelected = urlFilters[filter.title];
      if (!urlSelected?.size) return;

      if (!Array.isArray(filter.selected)) filter.selected = [];

      (filter.values || []).forEach((v) => {
        const label = typeof v === 'string' ? v : (v.label ?? '');
        if (
          urlSelected.has(label.toLowerCase()) &&
          !filter.selected.some((s) => String(s).toLowerCase() === label.toLowerCase())
        ) {
          filter.selected.push(label);
        }
      });
    });
  }

  function getActiveProductTypes() {
    // Prefer the SDK's own selection over the url: it is what the results were
    // actually built from, so the tabs stay honest even if the router has not
    // written the query string yet.
    const store = getSidebarStore();
    const filter = store ? findProductTypeFilter(store.filterArrayCollection) : null;
    if (filter) return (filter.selected ?? []).map((value) => String(value).toLowerCase());

    // URLSearchParams decodes '+' as a space, so the 'f.Product+Type' key reads
    // back as 'f.Product Type' and a value of 'lip+balm' as 'lip balm'. Multiple
    // values arrive comma-joined in one param, the way parseUrl() splits them.
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('f.Product Type');
    if (!raw) return [];
    return raw.split(',').map((value) => value.trim().toLowerCase());
  }

  function syncBubbleTabActiveState() {
    const activeProductTypes = getActiveProductTypes();

    // One selected type maps to one bubble. None → "All". More than one is only
    // reachable from the sidebar and matches no bubble.
    const soleActiveType = activeProductTypes.length === 1 ? activeProductTypes[0] : null;

    for (const bubble of document.querySelectorAll(BUBBLE_SELECTOR)) {
      // data-url-value keeps the '+' form (it used to be written straight into
      // the query string), so normalise both sides to spaces — otherwise every
      // multi-word product type (lip care, face wash, eye care) never matches.
      const urlValue = (bubble.dataset.urlValue ?? '').toLowerCase().replace(/\+/g, ' ');
      const isActive = urlValue === '' ? activeProductTypes.length === 0 : urlValue === soleActiveType;
      bubble.classList.toggle('active', isActive);
    }
  }

  function patchFilterBridge(store) {
    // Fix 1
    store.setCloseInitialContent = (val) => {
      if (!val) {
        store.closeInitialContent = false;
        return;
      }

      const wasLatched = store.closeInitialContent === true;
      store.closeInitialContent = true;

      // First transition — let the SDK's own watcher run so its full set of
      // side effects (grid reveal, mobile panel) still happens natively.
      if (!wasLatched) return;

      const proxy = getSearchPageProxy();
      if (proxy) {
        proxy.pushFilters();
        return;
      }

      // Proxy unreachable (SDK internals changed) — fall back to cycling the
      // flag so the watcher at least has a chance to re-fire.
      store.closeInitialContent = false;
      setTimeout(() => { store.closeInitialContent = true; }, 0);
    };

    // Fix 2 — reconcile filter checkboxes + bubble tabs from URL after every API response
    store.$onAction(({ name, after }) => {
      if (name !== 'setFilterArray') return;
      after(() => {
        syncFilterSelectionFromUrl(store);
        syncBubbleTabActiveState();
      });
    });
  }

  // ─── Bubble tab instant deactivation on selected-filter removal ───────────
  function initSelectedFilterRemoveSync() {
    // .tag-content = individual selected-filter chip; .st-last-clear-tag = "Clear All".
    // These chips may be rendered outside #collectionmodalcontainer so no containment
    // check here. The SDK updates the URL synchronously on click, so setTimeout(0)
    // is enough to read the already-updated URL and deactivate the tab instantly.
    document.addEventListener('click', (e) => {
      const chip = e.target.closest('.tag-content, .st-last-clear-tag');
      if (!chip) return;
      setTimeout(syncBubbleTabActiveState, 0);
    }, true);
  }

  // ─── Init Cart drawer patch ────────────────────────────────────────────────────
  let stAtcInProgress = false;
  let stActiveAtcBtn = null;

  function patchCartDrawer() {
    customElements.whenDefined('cart-drawer').then(() => {
      const CartDrawerClass = customElements.get('cart-drawer');
      if (!CartDrawerClass) return;
      const proto = CartDrawerClass.prototype;
      const orig = proto.renderContents;
      proto.renderContents = function (data) {
        const wasOpen = this.isOpen;
        orig.call(this, data);
        if (!wasOpen) this.open();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'searchtap' });
        }
        if (stAtcInProgress) {
          stAtcInProgress = false;
          const overlay = document.querySelector('[data-atc-screen-overlay]');
          if (overlay) overlay.style.display = 'none';
          if (stActiveAtcBtn) {
            stActiveAtcBtn.disabled = false;
            stActiveAtcBtn.style.opacity = '';
            stActiveAtcBtn.style.position = '';
            stActiveAtcBtn.querySelectorAll('.st-btn-loader-injected').forEach(el => el.remove());
            stActiveAtcBtn = null;
          }
        }
      };
    });
  }

  function patchSearchTapAtc() {
    const LOADER_CLASS = 'st-btn-loader-injected';
    const tpl = document.getElementById('dnk-btn-loader-template');
    const loaderInner = (() => {
      if (!tpl) return '';
      const div = document.createElement('div');
      div.appendChild(tpl.content.cloneNode(true));
      return div.innerHTML;
    })();
    const LOADER_HTML = `<span class="${LOADER_CLASS}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:inherit;border-radius:inherit;">${loaderInner}</span>`;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.devx_add_to_cart_button');
      if (!btn || !btn.closest('#searchModalContainer')) return;
      if (stAtcInProgress) return;

      stAtcInProgress = true;
      stActiveAtcBtn = btn;

      const overlay = document.querySelector('[data-atc-screen-overlay]');
      if (overlay) overlay.style.display = 'flex';
      document.querySelector('cart-drawer')?.open();

      requestAnimationFrame(() => {
        if (!stActiveAtcBtn) return;
        stActiveAtcBtn.disabled = true;
        stActiveAtcBtn.style.opacity = '0.5';
        stActiveAtcBtn.style.position = 'relative';
        if (!stActiveAtcBtn.querySelector('.' + LOADER_CLASS)) {
          stActiveAtcBtn.insertAdjacentHTML('beforeend', LOADER_HTML);
        }
      });
    }, true);
  }

  // ─── No-results filter hide ───────────────────────────────────────────────
  function initNoResultsFilterHide() {
    function syncNow() {
      const filterWrapper = document.querySelector('.st-collection-filter-wrapper');
      const noResultEl = document.querySelector('#searchModalContainer .st-no-result-text');
      if (!filterWrapper || !noResultEl) return;
      
      const isNoResults = noResultEl.offsetHeight > 0;
      filterWrapper.style.display = isNoResults ? 'none' : '';
      document.documentElement.classList.toggle('st-no-results-active', isNoResults);
    }

    const initObserver = new MutationObserver(() => {
      syncNow();
      if (document.querySelector('.st-collection-filter-wrapper') &&
          document.querySelector('#searchModalContainer .st-no-result-text')) {
        initObserver.disconnect();
      }
    });
    initObserver.observe(document.body, { childList: true, subtree: true });

    let initAttempts = 0;
    const initPoll = setInterval(() => {
      initAttempts++;
      syncNow();
      const ready = document.querySelector('.st-collection-filter-wrapper') &&
        document.querySelector('#searchModalContainer .st-no-result-text');
      if (ready || initAttempts >= 20) {
        clearInterval(initPoll);
        initObserver.disconnect();
      }
    }, 300);

    let syncTimer = null;
    whenStoreReady((store) => {
      store.$onAction(({ name, after }) => {
        if (name !== 'setFilterArray') return;
        after(() => {
          clearTimeout(syncTimer);
          // Vue's DOM patch runs in a microtask before setTimeout fires
          syncTimer = setTimeout(syncNow, 50);
        });
      });
    });
  }

  // ─── Best selling sort reset ──────────────────────────────────────────────
  function patchBestSellingSort(store) {
    store.$onAction(({ name, args }) => {
      // $onAction callback runs synchronously before the action — no separate
      // before() helper exists in Pinia.
      if (name !== 'setSortingValue') return;
      if (args[0] !== 'Best selling') return;
      if (store.sortingValue === 'Best selling') return; // already on default

      const proxy = getSearchPageProxy();
      if (proxy) {
        proxy.sortList('Best selling');
      }
    });
  }

  // ─── Sort dropdown label fix ─────────────────────────────────────────────
  function patchSortDropdownLabel(store) {
    let activeLabel = null;

    function applyLabel() {
      if (!activeLabel) return;

      document.querySelectorAll('.st-sort-box .st-tracking-normal').forEach((span) => {
        span.textContent = activeLabel;
      });

      document.querySelectorAll('.st-sorting li').forEach((li) => {
        const innerSpan = li.querySelector('span');
        const liLabel = innerSpan?.textContent?.trim();
        const isActive = liLabel === activeLabel;

        // <li> active: gray background + default cursor; inactive: pointer cursor
        li.classList.toggle('st-bg-[#f9f9f9]', isActive);
        li.classList.toggle('st-cursor-default', isActive);
        li.classList.toggle('st-cursor-pointer', !isActive);

        // inner <span> active: active-sort + default cursor; inactive: pointer cursor
        if (innerSpan) {
          innerSpan.classList.toggle('active-sort', isActive);
          innerSpan.classList.toggle('st-cursor-default', isActive);
          innerSpan.classList.toggle('st-cursor-pointer', !isActive);
        }
      });
    }

    store.$onAction(({ name, args, after }) => {
      if (name === 'setSortingValue') {
        activeLabel = args[0];
        after(() => setTimeout(applyLabel, 0));
        return;
      }
      if (name === 'setFilterArray' || name === 'setTotalHits') {
        after(() => { if (activeLabel) setTimeout(applyLabel, 0); });
      }
    });
  }

  // ─── Filter-clear button fix ──────────────────────────────────────────────
  function patchFilterClearButton(store) {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.filter-clear');
      if (!btn) return;
      if (!btn.closest('.st-collection-filter-wrapper')) return;

      setTimeout(() => {
        store.setCloseInitialContent(true);
      }, 0);
    }, true);
  }

  // ─── Mobile "Sort" pill fix ───────────────────────────────────────────────
  function patchMobileSortTabOpen() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.sort_h')) return;

      let frames = 0;
      const clickSortTab = () => {
        const tabs = [...document.querySelectorAll('.mobile-filter-title')];
        const sortTab = tabs.find(
          (tab) => tab.textContent.trim().toLowerCase().includes('sort') && tab.closest('.open')
        );
        if (sortTab) {
          sortTab.click();
          return;
        }
        if (++frames < MOBILE_SORT_TAB_MAX_FRAMES) requestAnimationFrame(clickSortTab);
      };
      requestAnimationFrame(clickSortTab);
    });
  }

  // ─── Mobile drawer open/close edges ───────────────────────────────────────
  // showMobileSort is local data on InitialCollectionFilters (data-v-c709cd25)
  // and nothing in the SDK clears it when the drawer closes, so whichever tab
  // the drawer was last left on survives into the next open. Both close
  // controls — the header ✕ and "Done" — run the same method:
  //
  //   props: ["isFilterOpen"],
  //   applyFilters(){ this.closeFilter(!1), this.isFilterOpen = !1, ... }
  //
  // isFilterOpen is a *prop*, so that assignment throws ("'set' on proxy: trap
  // returned falsish for property 'isFilterOpen'") and every statement after it
  // is dead code. The drawer still closes only because closeFilter(false)
  // already emitted up to InitialCollectionToolBar, which owns the real
  // isFilterOpen. So no SDK code path survives that could reset the tab.
  //
  // Result: once patchMobileSortTabOpen() above switches the drawer to Sort By
  // and the user closes it without picking a sort value, tapping "Filter"
  // reopens straight onto .st-sorting-wrapper — the MobileSort child is
  // v-if="showMobileSort" and re-mounts as a fixed overlay covering the filter
  // list. Picking a sort value hides the bug, because sortList() does clear the
  // flag on a timer.
  //
  // Fix: when a drawer loses its `open` class, click its own "Filters" tab
  // (onClick: showMobileSort = false) while it is still mounted. Resetting on
  // close rather than on the next open means no flash of the sort panel, and
  // keying off the class covers every close path instead of relying on us to
  // enumerate them.
  //
  // The open edge marks <html> for the length of the drawer's slide. The sort
  // panel's slide-in (searchtap-collection.css) has to be a keyframe animation
  // rather than a transition, and keyframes replay every time the v-if'd node
  // mounts — including a Filters → Sort By switch inside a drawer that is
  // already open and stationary, where sliding reads as a second, unrelated
  // animation. Gating on the marker limits it to the case it exists for: the
  // panel arriving together with the drawer.

  const DRAWER_OPENING_CLASS = 'st-drawer-opening';
  const MOBILE_DRAWER_SLIDE_MS = 300;

  function resetMobileSortTab(drawer) {
    const tabs = [...drawer.querySelectorAll('.mobile-filter-title')];
    const filtersTab = tabs.find((tab) => tab.textContent.trim().toLowerCase().includes('filter'));
    filtersTab?.click();
  }

  function patchMobileDrawerEdges() {
    let openingTimer = null;

    // The marker goes on <html>, not the drawer: Vue owns the drawer's class
    // attribute and would drop anything we add there on its next patch.
    const markOpening = () => {
      document.documentElement.classList.add(DRAWER_OPENING_CLASS);
      clearTimeout(openingTimer);
      openingTimer = setTimeout(() => {
        document.documentElement.classList.remove(DRAWER_OPENING_CLASS);
      }, MOBILE_DRAWER_SLIDE_MS);
    };

    // One delegated observer rather than a discovery scan plus one observer per
    // drawer: attributeOldValue carries the previous class list, which is the
    // only state needed to spot both edges. That also covers the drawers
    // mounting late (the SDK is idle-loaded on shop-all) and the second copy
    // SearchPage renders, with no rescanning.
    new MutationObserver((mutations) => {
      mutations.forEach(({ target, oldValue }) => {
        if (!target.classList.contains('mobilesearch')) return;
        const wasOpen = (oldValue ?? '').split(/\s+/).includes('open');
        const isOpen = target.classList.contains('open');
        if (!wasOpen && isOpen) markOpening();
        if (!wasOpen || isOpen) return;

        resetMobileSortTab(target);

        // Two drawer copies exist, and SearchPage's watcher
        //   closeInitialContent(t){ t && (..., isDeviceMobile() && (this.isFilterOpen = !0)) }
        // opens its own on the first filter apply with nothing ever setting it
        // back. Closing the copy the user can see then just reveals the other,
        // which is why "Done" appeared to need two taps once a filter had been
        // applied. `target` has already lost `open` here, so this only matches
        // the other copies.
        if (document.querySelector('.mobilesearch.open')) closeMobileFilterDrawer();
      });
    }).observe(document.getElementById('collectionmodalcontainer') ?? document.body, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
      subtree: true,
    });
  }

  // ─── Close the mobile drawer when a sort option is picked ─────────────────
  // Choosing a sort value runs InitialCollectionFilters.sortList(), which sets
  // the store value and flips the tab back to Filters after 100 ms but leaves
  // the drawer open over the results the user just re-ordered. A sort is a
  // single choice, so the drawer should close with it.
  //
  // This is deliberately NOT symmetric with the Filters tab. There, picking a
  // value is one step of a multi-select, so the drawer stays open and closes
  // only through the SDK's own "Done" (.apply-btn) / header ✕ — both of which
  // run applyFilters(), whose this.closeFilter(false) emits to
  // InitialCollectionToolBar (onCloseFilter: t.isFilterOpen = false) before the
  // line that throws, so that path already works untouched.
  //
  // Hooking the sort *button* rather than the setSortingValue action matters:
  // patchMobileResetSort() below also calls sortList('Best selling'), and Reset
  // must leave the drawer open.

  // isFilterOpen is data() on SearchPage and InitialCollectionToolBar, but a
  // readonly prop on InitialCollectionFilters — assigning to that prop is what
  // makes applyFilters() throw. Writing to the reactive data object of whichever
  // instances actually own it avoids the prop entirely and closes both drawer
  // copies without having to work out which one is on screen. Synthesising a ✕
  // click is not an option here either: .apply-all is v-if="!showMobileSort", so
  // the apply bar is not even rendered while the sort tab is up.
  function closeMobileFilterDrawer() {
    const rootVNode = document.getElementById('collectionmodalcontainer')?._vnode;
    if (!rootVNode?.component) return;

    const walk = (vnode) => {
      if (!vnode || typeof vnode !== 'object') return;
      if (vnode.component) {
        const { data } = vnode.component;
        if (data && 'isFilterOpen' in data) data.isFilterOpen = false;
        walk(vnode.component.subTree);
      }
      if (Array.isArray(vnode.children)) vnode.children.forEach(walk);
    };
    walk(rootVNode.component.subTree);

    // closeFilter() clears this on the SDK's own close path; do the same here.
    document.body.classList.remove('st-open-filter-section');
  }

  function patchCloseDrawerOnSortSelect() {
    document.addEventListener('click', (e) => {
      const option = e.target.closest('.st-sorting-wrapper .ripple-container button');
      if (!option?.closest('.mobilesearch.open')) return;

      // Defer past Vue's flush so the close is not overwritten by a watcher
      // reacting to the same setSortingValue call.
      setTimeout(closeMobileFilterDrawer, 0);
    });
  }

  // ─── Mobile "Reset" button sort fix ───────────────────────────────────────
  function patchMobileResetSort() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.st-reset-all-mobile')) return;
      setTimeout(() => {
        const proxy = getSearchPageProxy();
        if (proxy) proxy.sortList('Best selling');
      }, 0);
    });
  }

  // ─── Show content on initial desktop load ────────────────────────────────
  function markSearchtapActive() {
    const grid = document.querySelector('.searchtap-collection-product-grid');
    if (grid) grid.classList.add('st-searchtap-active');
  }

  function patchShowContent(store) {
    const revealSearchtapGrid = () => {
      const proxy = getSearchPageProxy();
      if (!proxy) return;

      if (!proxy.showSearchtapContent) {
        
        const { search } = window.location;
        if (search.includes('f.') || search.includes('sort_by=')) {
          proxy.showSearchtapContent = true;
          paginationObserver?.disconnect();
          const temp = document.querySelector('.searchtap-temp');
          if (temp) temp.innerHTML = '';
          const pagination = document.getElementById('product-grid-Pagination');
          if (pagination) pagination.innerHTML = '';
          markSearchtapActive();
        }
      }

      setTimeout(() => {
        if (getSearchPageProxy()?.showSearchtapContent) markSearchtapActive();
      }, 50);
    };

    revealSearchtapGrid();

    store.$onAction(({ name, after }) => {
      if (name !== 'setTotalHits') return;
      after(revealSearchtapGrid);
    });
  }

  // ─── Product offer text overrides ────────────────────────────────────────
  const OFFER_OVERRIDES = {
    // '10449879597221': 'Flat 20% Off',
    // '8200966963365': 'Flat 20% Off',
  };

  function applyOfferOverrides() {
    const container = document.getElementById('collectionmodalcontainer');
    if (!container || container.dataset.saleMode !== 'product') return;
    Object.keys(OFFER_OVERRIDES).forEach((productId) => {
      document.querySelectorAll(`[data-product-id="${productId}"]`).forEach((card) => {
        const offerEl = card.querySelector('.st-offer');
        if (!offerEl) return;
        if (offerEl.textContent.trim() !== OFFER_OVERRIDES[productId]) {
          offerEl.textContent = OFFER_OVERRIDES[productId];
        }
      });
    });
  }

  function initOfferOverrideObserver() {
    const container = document.getElementById('collectionmodalcontainer');
    if (!container) return;
    applyOfferOverrides();
    const observer = new MutationObserver(applyOfferOverrides);
    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Swatch carousel ──────────────────────────────────────────────────────
  function applySwatchCarousels() {
    document.querySelectorAll('#searchModalContainer .st-swatch-border').forEach((swatch) => {
      const parent = swatch.parentElement;
      if (!parent || parent.classList.contains('st-swatch-carousel')) return;
      parent.classList.add('st-swatch-carousel');
    });
  }

  function initSwatchCarouselObserver() {
    // Collection page — container is already in the DOM on load.
    const collectionContainer = document.getElementById('collectionmodalcontainer');
    if (collectionContainer) {
      applySwatchCarousels();
      const observer = new MutationObserver(applySwatchCarousels);
      observer.observe(collectionContainer, { childList: true, subtree: true });
      return;
    }

    // Search page — #searchModalContainer is injected by the SDK after DOMContentLoaded.
    function hookSearchContainer(container) {
      applySwatchCarousels();
      const observer = new MutationObserver(applySwatchCarousels);
      observer.observe(container, { childList: true, subtree: true });
    }

    const searchContainer = document.getElementById('searchModalContainer');
    if (searchContainer) {
      hookSearchContainer(searchContainer);
      return;
    }

    // SDK hasn't injected #searchModalContainer yet — watch body for it.
    const bodyObserver = new MutationObserver(() => {
      const container = document.getElementById('searchModalContainer');
      if (!container) return;
      bodyObserver.disconnect();
      hookSearchContainer(container);
    });
    bodyObserver.observe(document.body, { childList: true });
  }

  // ─── Fix "FIlter" typo baked into searchtap.js Vue template ─────────────────
  function fixFilterLabelTypo() {
    function patch() {
      let fixed = false;
      document.querySelectorAll('span.disInBlock > span').forEach((span) => {
        span.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.includes('FIlter')) {
            node.textContent = node.textContent.replace('FIlter', 'Filter');
            fixed = true;
          }
        });
      });
      return fixed;
    }

    if (patch()) return;

    // SearchTap renders asynchronously — observe for DOM changes and also
    // poll with a short timeout as a fallback for slow renders.
    const observer = new MutationObserver(() => {
      if (patch()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Fallback: retry every 300 ms for up to 6 seconds.
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (patch() || attempts >= 20) {
        clearInterval(poll);
        observer.disconnect();
      }
    }, 300);
  }

  // ─── Bubble filter height CSS variable ───────────────────────────────────
  function initBubbleFilterHeightVar() {
    const wrap = document.querySelector('.searchtap_bubble_filter_wrap');
    if (!wrap) return;

    const sync = () => {
      document.documentElement.style.setProperty(
        '--bubble-filter-wrap-height',
        `${wrap.offsetHeight}px`
      );
    };

    sync();
    new ResizeObserver(sync).observe(wrap);
  }

  // ─── Promotion banner → Combo tab ────────────────────────────────────────
  // The shop-all promo banners sit inside the Liquid grid and act as a shortcut
  // to the "Combo" bubble tab — one banner for desktop, one for mobile, both
  // carrying .combo-filter-trigger. Dispatching the click on the bubble itself
  // reuses initBubbleTabs() wholesale — active-state marking and the retry
  // included — instead of re-implementing them.
  function initComboBannerTrigger() {
    const BANNER_SELECTOR = '.combo-filter-trigger';
    const COMBO_BUBBLE_SELECTOR = `${BUBBLE_SELECTOR}[data-filter="combo"]`;

    // .result-mob is rendered by the SDK, so it only exists once the combo
    // results come back. Poll for it, then fall back to the SDK grid host.
    const scrollToResults = () => {
      const deadline = Date.now() + 4000;

      const step = () => {
        const results = document.querySelector('.result-mob');
        if (results) {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (Date.now() < deadline) {
          setTimeout(step, 100);
          return;
        }
        document.querySelector('searchtap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      step();
    };

    const activateComboTab = () => {
      const bubble = document.querySelector(COMBO_BUBBLE_SELECTOR);
      if (!bubble) return;

      bubble.click();
      scrollToResults();
    };

    document.addEventListener('click', (event) => {
      if (!event.target.closest(BANNER_SELECTOR)) return;
      activateComboTab();
    });

    // The banner is a div with role="button", so Enter/Space have to be wired up.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest(BANNER_SELECTOR)) return;

      event.preventDefault();
      activateComboTab();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initBubbleTabs();
    initComboBannerTrigger();
    initBubbleFilterHeightVar();
    initPaginationObserver();
    patchCartDrawer();
    patchSearchTapAtc();
    initNoResultsFilterHide();
    patchMobileSortTabOpen();
    patchMobileDrawerEdges();
    patchCloseDrawerOnSortSelect();
    patchMobileResetSort();

    // These five all drive the SDK through its Pinia store, which only exists
    // once the idle-loaded bundle has mounted the Vue app.
    whenStoreReady((store) => {
      patchFilterBridge(store);
      patchSortDropdownLabel(store);
      patchBestSellingSort(store);
      patchFilterClearButton(store);
      patchShowContent(store);
    });

    initSwatchCarouselObserver();
    initOfferOverrideObserver();
    initSelectedFilterRemoveSync();
    fixFilterLabelTypo();
  });
})();
